import { supabase } from "@/integrations/supabase/client";

export interface COSUploadOptions {
  file: File;
  folder?: string;
  onProgress?: (percent: number) => void;
  ciProcess?: string;
}

export interface COSUploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

function normalizeCdnUrl(url: string): string {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (!/\.cos\.[^.]+\.myqcloud\.com$/i.test(parsed.hostname)) {
      return url;
    }

    const configuredHost = String(
      import.meta.env.VITE_TENCENT_CDN_DOMAIN || import.meta.env.VITE_CDN_DOMAIN || ""
    )
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/\/+$/, "");

    const runtimeHost =
      typeof window !== "undefined" ? String(window.location.hostname || "").trim() : "";

    const targetHost = configuredHost || runtimeHost;
    if (
      !targetHost ||
      targetHost === "localhost" ||
      targetHost === "127.0.0.1" ||
      targetHost === "::1"
    ) {
      return url;
    }

    parsed.protocol = "https:";
    parsed.hostname = targetHost;
    parsed.port = "";
    return parsed.toString();
  } catch {
    return url;
  }
}

async function readResponsePayload(response: unknown): Promise<string | null> {
  const r = response as { clone?: () => Response; text?: () => Promise<string> } | undefined;
  const res = r?.clone ? r.clone() : (r as unknown as Response | undefined);
  if (!res?.text) return null;
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      const json = JSON.parse(text) as unknown;
      const message =
        (json as { error?: unknown })?.error ??
        (json as { message?: unknown })?.message ??
        (json as { msg?: unknown })?.msg;
      if (typeof message === "string" && message.trim()) return message.trim();
      return text.length > 800 ? text.slice(0, 800) + "…" : text;
    } catch {
      return text.length > 800 ? text.slice(0, 800) + "…" : text;
    }
  } catch {
    return null;
  }
}

function stringifyUnknownBody(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === "string") return body.trim() || null;
  if (typeof body === "number" || typeof body === "boolean") return String(body);
  try {
    return JSON.stringify(body);
  } catch {
    return null;
  }
}

function extractMessageFromJsonLike(value: unknown): string | null {
  const v = value as { error?: unknown; message?: unknown; msg?: unknown } | undefined;
  const message = v?.error ?? v?.message ?? v?.msg;
  if (typeof message === "string" && message.trim()) return message.trim();
  return null;
}

async function formatInvokeError(err: unknown): Promise<string> {
  const e = err as {
    message?: unknown;
    code?: unknown;
    details?: unknown;
    hint?: unknown;
    context?: unknown;
  };
  const base = typeof e?.message === "string" && e.message.trim() ? e.message.trim() : "调用失败";
  const code = typeof e?.code === "string" && e.code.trim() ? e.code.trim() : null;
  const details = typeof e?.details === "string" && e.details.trim() ? e.details.trim() : null;
  const hint = typeof e?.hint === "string" && e.hint.trim() ? e.hint.trim() : null;
  const ctx = e?.context as { response?: unknown; status?: unknown; statusText?: unknown; body?: unknown } | undefined;
  const response = ctx?.response;
  const responsePayload = response ? await readResponsePayload(response) : null;
  const statusFromResponse = (response as { status?: unknown } | undefined)?.status;
  const statusTextFromResponse = (response as { statusText?: unknown } | undefined)?.statusText;
  const status = typeof statusFromResponse === "number" ? statusFromResponse : (typeof ctx?.status === "number" ? ctx.status : undefined);
  const statusText =
    typeof statusTextFromResponse === "string"
      ? statusTextFromResponse
      : typeof ctx?.statusText === "string"
        ? ctx.statusText
        : undefined;

  const ctxBodyText = stringifyUnknownBody(ctx?.body);
  let ctxBodyMessage: string | null = null;
  if (ctxBodyText) {
    try {
      ctxBodyMessage = extractMessageFromJsonLike(JSON.parse(ctxBodyText));
    } catch {
      ctxBodyMessage = null;
    }
  }
  const bodyMessage = ctxBodyMessage || ctxBodyText || responsePayload;

  const parts: string[] = [];
  if (code) parts.push(code);
  if (typeof status === "number") parts.push(statusText ? `${status} ${String(statusText)}` : String(status));
  if (bodyMessage) parts.push(bodyMessage);
  if (details) parts.push(details);
  if (hint) parts.push(hint);

  return parts.length ? `${base}（${parts.join(" / ")}）` : base;
}

/**
 * Upload file to Tencent Cloud COS via Edge Function
 * Uses presigned URL for direct upload from browser
 */
export async function uploadToCOS({
  file,
  folder = "videos",
  onProgress,
  ciProcess,
}: COSUploadOptions): Promise<COSUploadResult> {
  try {
    // Step 1: Get presigned URL from edge function
    onProgress?.(5);
    
    const { data: presignData, error: presignError } = await supabase.functions.invoke(
      "upload-to-cos",
      {
        body: {
          action: "getPresignedUrl",
          fileName: file.name,
          fileType: file.type,
          folder,
          ciProcess,
        },
      }
    );

    if (presignError || !presignData?.success) {
      const detail = presignError ? await formatInvokeError(presignError) : null;
      throw new Error(presignData?.error || detail || "获取上传地址失败");
    }

    const { presignedUrl, cdnUrl, key } = presignData;

    // Step 2: Upload directly to COS using presigned URL
    onProgress?.(10);

    const xhr = new XMLHttpRequest();
    
    const uploadPromise = new Promise<void>((resolve, reject) => {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          // Map 10-95% to upload progress
          const percent = 10 + (e.loaded / e.total) * 85;
          onProgress?.(Math.round(percent));
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`上传失败: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("网络错误，上传失败"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("上传已取消"));
      });
    });

    xhr.open("PUT", presignedUrl);
    
    // Add CI process header if provided
    if (ciProcess) {
      xhr.setRequestHeader("x-cos-ci-process", ciProcess);
    }
    
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.send(file);

    await uploadPromise;
    onProgress?.(100);

    return {
      success: true,
      url: normalizeCdnUrl(cdnUrl),
      key,
    };
  } catch (error) {
    console.error("COS upload error:", error);
    const detail = await formatInvokeError(error);
    return {
      success: false,
      error: detail,
    };
  }
}

/**
 * Upload thumbnail to COS
 */
export async function uploadThumbnailToCOS(
  file: File,
  onProgress?: (percent: number) => void
): Promise<COSUploadResult> {
  return uploadToCOS({
    file,
    folder: "thumbnails",
    onProgress,
  });
}
